/** Plain-words formatting. Nothing here rounds a number into a claim it cannot carry. */
export function fmtDuration(ms: number | null | undefined): string {
  if (!ms || ms <= 0) return "";
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h > 0) return `${h} h ${String(m).padStart(2, "0")} m`;
  const s = total % 60;
  return m > 0 ? `${m} m` : `${s} s`;
}

/** mm:ss or h:mm:ss for a position inside the video; `hours` keeps the hour under one (0:54:24 beside 1:10:00). */
export function fmtClock(ms: number, hours = false): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 || hours ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`;
}

/** "0:54:24 – 3:15:48": a part of the upload; `hours` keeps the hour on a part that ends under one (a list of parts of a long video). */
export function fmtSpan(seg: { start_ms: number; end_ms: number }, hours = seg.end_ms >= 3_600_000): string {
  return `${fmtClock(seg.start_ms, hours)} – ${fmtClock(seg.end_ms, hours)}`;
}

export function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function fmtBytes(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(n >= 1e10 ? 0 : 1)} GB`;
  if (n >= 1e6) return `${Math.round(n / 1e6)} MB`;
  if (n >= 1e3) return `${Math.round(n / 1e3)} KB`;
  return `${n} B`;
}

/** "by Wednesday", "in about 3 hours", "within a day". */
export function fmtWait(seconds: number | null | undefined, from = new Date()): string {
  if (seconds == null) return "";
  if (seconds < 3600) return "within the hour";
  if (seconds < 6 * 3600) return `in about ${Math.round(seconds / 3600)} hours`;
  const eta = new Date(from.getTime() + seconds * 1000);
  const days = Math.round((eta.getTime() - from.getTime()) / 86_400_000);
  if (days <= 0) return "later today";
  if (days === 1) return "by tomorrow";
  if (days < 7) return `by ${eta.toLocaleDateString(undefined, { weekday: "long" })}`;
  return `in about ${days} days`;
}

export function fmtExpiry(iso: string | null, now = new Date()): string {
  if (!iso) return "";
  const days = Math.ceil((new Date(iso).getTime() - now.getTime()) / 86_400_000);
  if (days <= 0) return "expires today";
  if (days === 1) return "expires tomorrow";
  return `expires ${fmtDate(iso)}`;
}

export function fmtMoney(amountMinor: number, currency: string): string {
  const major = amountMinor / 100;
  const sym = currency === "INR" ? "₹" : currency === "USD" ? "$" : `${currency} `;
  return `${sym}${major.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: major % 1 ? 2 : 0 })}`;
}

/** "Sunday_XI-vs-Riverside.mp4" -> "Sunday XI vs Riverside" */
export function titleFromFilename(name: string): string {
  return name.replace(/\.[^.]+$/, "").replace(/[_\-]+/g, " ").replace(/\s+/g, " ").trim();
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}
